import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Archive, ArchiveRestore, Check, Copy, Folder, FolderMinus, MoreHorizontal, Users } from 'lucide-react'
import type { Board, BoardFolder, BoardFolderTree } from '@/api/boardsApi'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { folderOf } from '@/lib/boardTree'
import { cn } from '@/lib/utils'

const item =
  'flex w-full items-center gap-2 rounded-sm px-2.5 py-1.5 text-left text-meta text-ink-body hover:bg-surface-subtle focus-visible:bg-surface-subtle focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50'
const heading = 'px-2.5 pb-1 pt-2 text-eyebrow font-medium uppercase text-ink-muted'

export interface BoardMenuActions {
  moveTo: (b: Board, scope: 'personal' | 'team', folderId: string | null) => void
  duplicate: (b: Board) => void
  setArchived: (b: Board, archived: boolean) => void
}

/** A board's "…" menu: file it in my folders or my team's, duplicate, archive / restore. */
export function BoardMenu({ board, folders, actions, className }: {
  board: Board
  folders: BoardFolderTree | undefined
  actions: BoardMenuActions
  className?: string
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const run = (fn: () => void) => () => {
    setOpen(false)
    fn()
  }
  const team = folders?.team ?? null
  // A team folder holds boards shared with that team only (the API says the same).
  const teamOk = !!team && board.visibility === 'shared' && board.team?.id === team.id
  const group = (scope: 'personal' | 'team', list: BoardFolder[], label: string, disabledNote?: string) => {
    const current = folderOf(list, board.id)
    const Icon = scope === 'team' ? Users : Folder
    return (
      <div role="group" aria-label={label}>
        <p className={heading}>{label}</p>
        {disabledNote ? <p className="px-2.5 pb-1 text-label text-ink-muted">{disabledNote}</p> : null}
        {list.map((f) => (
          <button key={f.id} type="button" disabled={!!disabledNote} aria-current={current?.id === f.id ? true : undefined} className={item} onClick={run(() => actions.moveTo(board, scope, f.id))}>
            <Icon className="size-3.5 shrink-0 text-ink-muted" aria-hidden />
            <span className="min-w-0 grow truncate">{f.name}</span>
            {current?.id === f.id ? <Check className="size-3.5 text-primary" aria-hidden /> : null}
          </button>
        ))}
        {current ? (
          <button type="button" className={item} onClick={run(() => actions.moveTo(board, scope, null))}>
            <FolderMinus className="size-3.5 shrink-0 text-ink-muted" aria-hidden />
            {t('boards.takeOutOf', { folder: current.name })}
          </button>
        ) : null}
      </div>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('boards.more', { name: board.name })}
          title={t('boards.more', { name: board.name })}
          className={cn('rounded-md p-1 text-ink-muted hover:bg-surface-subtle hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', className)}
        >
          <MoreHorizontal className="size-4" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="max-h-[28rem] w-64 overflow-y-auto p-1">
        {board.archivedAt ? null : (
          <>
            {folders?.personal.length ? group('personal', folders.personal, t('boards.myFolders')) : null}
            {team?.folders.length
              ? group('team', team.folders, t('boards.teamFolders', { team: team.name }), teamOk ? undefined : t('boards.teamNeedsShared'))
              : null}
            {folders?.personal.length || team?.folders.length ? <div className="my-1 border-t border-line" /> : null}
          </>
        )}
        <button type="button" className={item} onClick={run(() => actions.duplicate(board))}>
          <Copy className="size-3.5 shrink-0 text-ink-muted" aria-hidden />
          {t('boards.duplicate')}
        </button>
        {board.mine ? (
          <button type="button" className={item} onClick={run(() => actions.setArchived(board, !board.archivedAt))}>
            {board.archivedAt ? (
              <ArchiveRestore className="size-3.5 shrink-0 text-ink-muted" aria-hidden />
            ) : (
              <Archive className="size-3.5 shrink-0 text-ink-muted" aria-hidden />
            )}
            {board.archivedAt ? t('boards.restore') : t('boards.archive')}
          </button>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}
